import { Outlet, useNavigate, useLocation, matchPath } from 'react-router-dom';
import { Button } from '@dreamtree-org/twreact-ui';
import { Store, Wrench } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import { useAuth } from '@rbac/useCan';
import { clearAuth } from '@rbac/permissions';
import { signOutThroughDreamtree } from '@/lib/dreamtreeSso';
import { StaffShell } from '@/components';

/**
 * Service portal shell (StaffShell / twreact AppShell). The technician PWA is phone-first: every
 * nav route is in the drawer (so Parts is reachable on phones), `foot: true` routes make the tab bar.
 * `routes`: the screens this user may open in this store (visibleServiceRoutes).
 */
export default function ServiceLayout({ store, routes = [] }) {
  const nav = useNavigate();
  const loc = useLocation();
  const user = useAuth();
  const { fetchApi } = useAppApi();

  // A route is active on its own path and on the detail paths it owns (`match`).
  const isActive = (r) => [r.path, ...(r.match || [])].some((p) => matchPath({ path: p, end: true }, loc.pathname));
  const navRoutes = routes.filter((r) => r.nav);
  const current = navRoutes.find(isActive);
  const icon = (r, size) => (r.icon ? <r.icon size={size} aria-hidden /> : null);
  const sidebarItems = navRoutes.map((r) => ({ id: r.path, label: r.label, href: r.path, icon: icon(r, 18), active: isActive(r) }));
  const footItems = navRoutes.filter((r) => r.foot).map((r) => ({ id: r.path, label: r.short || r.label, to: r.path, icon: icon(r, 20), active: isActive(r) }));
  const storeName = (store && store.name) || 'MerchantPro';

  const logout = async () => {
    // A Dreamtree ID session (DTID-44) signs out at the IdP too.
    if (user && user.sso) {
      clearAuth();
      signOutThroughDreamtree();
      return;
    }
    try {
      await fetchApi('logout');
    } catch {
      /* the session may already be gone */
    }
    clearAuth();
    nav('/login', { replace: true });
  };

  return (
    <StaffShell
      brand={
        <span className="min-w-0">
          <span className="block font-semibold truncate">{storeName}</span>
          <span className="inline-flex items-center gap-1 text-micro opacity-70"><Wrench size={12} aria-hidden /> Service</span>
        </span>
      }
      pageTitle={current ? current.label : 'Service'}
      nav={sidebarItems}
      foot={footItems}
      user={user}
      onLogout={logout}
      actions={
        <Button as="a" href="/admin" variant="ghost" size="icon" aria-label="Back office" title="Back office">
          <Store size={18} aria-hidden />
        </Button>
      }
    >
      <Outlet />
    </StaffShell>
  );
}
